// client/src/components/Logs/LogForm.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Box,
  CircularProgress,
  Autocomplete,
  Chip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../api/axiosConfig';

const createLog = async (data) => {
  const response = await axios.post('/logs', data);
  return response.data;
};

const updateLog = async ({ id, data }) => {
  const response = await axios.put(`/logs/${id}`, data);
  return response.data;
};

const changeLogStatus = async ({ id, status, notes }) => {
  const response = await axios.patch(`/logs/${id}/status`, { status, notes });
  return response.data;
};

const fetchClients = async () => {
  const response = await axios.get('/clients');
  return response.data;
};

const fetchVehicles = async () => {
  const response = await axios.get('/vehicles');
  return response.data;
};

const fetchDevices = async () => {
  const response = await axios.get('/devices');
  return response.data;
};

const fetchWorkOrders = async () => {
  const response = await axios.get('/workorders');
  return response.data;
};

const fetchUsers = async () => {
  const response = await axios.get('/users');
  return response.data;
};

const LogForm = ({ open, onClose, log, onSuccess, onCancel }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    clientId: '',
    vehicleId: '',
    deviceId: '',
    workOrderId: '',
    assignedTo: '',
    status: 'new',
    notes: '',
  });
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  // Загрузка данных
  const { data: clients, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: fetchClients,
    enabled: open,
  });

  const { data: vehicles, isLoading: vehiclesLoading } = useQuery({
    queryKey: ['vehicles'],
    queryFn: fetchVehicles,
    enabled: open,
  });

  const { data: allDevices, isLoading: devicesLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
    enabled: open,
  });

  const { data: workOrders, isLoading: workOrdersLoading } = useQuery({
    queryKey: ['workorders'],
    queryFn: fetchWorkOrders,
    enabled: open,
  });

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
    enabled: open,
  });

  // Фильтрация устройств в зависимости от выбранного транспорта
  const filteredDevices = useMemo(() => {
    if (!allDevices) return [];
    
    if (form.vehicleId) {
      // Если выбран транспорт, показываем устройства, привязанные к нему
      return allDevices.filter(device => 
        device.vehicleId?._id === form.vehicleId || device.vehicleId === form.vehicleId
      );
    }
    // Если транспорт не выбран, показываем все устройства
    return allDevices;
  }, [allDevices, form.vehicleId]);

  // Фильтрация транспорта по выбранному клиенту
  const filteredVehicles = useMemo(() => {
    if (!vehicles) return [];
    if (!form.clientId) return vehicles;
    return vehicles.filter(v => {
      const vehicleClientId = v.clientId?._id || v.clientId;
      return vehicleClientId === form.clientId;
    });
  }, [vehicles, form.clientId]);

  // Заполнение формы при редактировании
  useEffect(() => {
    if (log && open) {
      setForm({
        title: log.title || '',
        description: log.description || '',
        clientId: log.clientId?._id || log.clientId || '',
        vehicleId: log.vehicleId?._id || log.vehicleId || '',
        deviceId: log.deviceId?._id || log.deviceId || '',
        workOrderId: log.workOrderId?._id || log.workOrderId || '',
        assignedTo: log.assignedTo?._id || log.assignedTo || '',
        status: log.status || 'new',
        notes: '',
      });
    } else if (open) {
      setForm({
        title: '',
        description: '',
        clientId: '',
        vehicleId: '',
        deviceId: '',
        workOrderId: '',
        assignedTo: '',
        status: 'new',
        notes: '',
      });
      setError('');
    }
  }, [log, open]);

  // Мутации
  const updateMutation = useMutation({
    mutationFn: updateLog,
    onSuccess: () => {
      queryClient.invalidateQueries(['logs']);
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    },
    onError: (err) => {
      console.error('Update error:', err);
      setError(err.response?.data?.message || 'Error saving log');
    },
  });

  const statusMutation = useMutation({
    mutationFn: changeLogStatus,
    onSuccess: () => {
      queryClient.invalidateQueries(['logs']);
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    },
    onError: (err) => {
      console.error('Status change error:', err);
      setError(err.response?.data?.message || 'Error updating status');
    },
  });

  const createMutation = useMutation({
    mutationFn: createLog,
    onSuccess: () => {
      queryClient.invalidateQueries(['logs']);
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    },
    onError: (err) => {
      console.error('Create error:', err);
      setError(err.response?.data?.message || 'Error creating log');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title) {
      setError('Title is required');
      return;
    }

    const submitData = {
      title: form.title,
      description: form.description || '',
      clientId: form.clientId || null,
      vehicleId: form.vehicleId || null,
      deviceId: form.deviceId || null,
      workOrderId: form.workOrderId || null,
      assignedTo: form.assignedTo || null,
    };

    if (log) {
      updateMutation.mutate({ id: log._id, data: submitData });
      
      if (form.status !== log.status || (form.notes && form.notes.trim() !== '')) {
        statusMutation.mutate({
          id: log._id,
          status: form.status,
          notes: form.notes || `Status changed from ${log.status} to ${form.status}`,
        });
      }
    } else {
      createMutation.mutate({
        ...submitData,
        status: form.status,
        notes: form.notes || 'Initial creation',
      });
    }
  };

  const handleClientChange = (clientId) => {
    setForm({ 
      ...form, 
      clientId, 
      vehicleId: '', 
      deviceId: '' 
    });
  };

  const handleVehicleChange = (vehicleId) => {
    setForm({ 
      ...form, 
      vehicleId, 
      deviceId: '' 
    });
  };

  const isLoading = clientsLoading || vehiclesLoading || devicesLoading || workOrdersLoading || usersLoading;
  const isSaving = updateMutation.isPending || statusMutation.isPending || createMutation.isPending;

  // Получение выбранного устройства для отображения в Autocomplete
  const selectedDevice = useMemo(() => {
    if (!form.deviceId || !allDevices) return null;
    return allDevices.find(d => d._id === form.deviceId);
  }, [form.deviceId, allDevices]);

  // Получение выбранного транспорта для отображения в Autocomplete
  const selectedVehicle = useMemo(() => {
    if (!form.vehicleId || !vehicles) return null;
    return vehicles.find(v => v._id === form.vehicleId);
  }, [form.vehicleId, vehicles]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{log ? 'Edit Log Entry' : 'Create Log Entry'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Client</InputLabel>
                  <Select
                    value={form.clientId || ''}
                    onChange={(e) => handleClientChange(e.target.value)}
                    label="Client"
                  >
                    <MenuItem value="">None</MenuItem>
                    {clients?.map((client) => (
                      <MenuItem key={client._id} value={client._id}>
                        {client.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Vehicle</InputLabel>
                  <Select
                    value={form.vehicleId || ''}
                    onChange={(e) => handleVehicleChange(e.target.value)}
                    label="Vehicle"
                    disabled={!form.clientId}
                  >
                    <MenuItem value="">None</MenuItem>
                    {filteredVehicles?.map((vehicle) => (
                      <MenuItem key={vehicle._id} value={vehicle._id}>
                        {vehicle.licensePlate} - {vehicle.brand} {vehicle.model} ({vehicle.year})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  options={filteredDevices}
                  getOptionLabel={(option) => {
                    const vehicleInfo = option.vehicleId?.licensePlate 
                      ? ` (ТС: ${option.vehicleId.licensePlate})` 
                      : ' (не установлен)';
                    return `${option.imei} - ${option.type}${vehicleInfo}`;
                  }}
                  value={selectedDevice}
                  onChange={(event, newValue) => {
                    setForm({ ...form, deviceId: newValue?._id || '' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Device"
                      placeholder="Поиск по IMEI, типу или ТС..."
                      helperText={
                        form.vehicleId 
                          ? "Показаны устройства, установленные на выбранном ТС" 
                          : "Показаны все устройства"
                      }
                    />
                  )}
                  renderOption={(props, option) => {
                    const vehicleInfo = option.vehicleId?.licensePlate 
                      ? `ТС: ${option.vehicleId.licensePlate}` 
                      : 'Не установлен';
                    return (
                      <li {...props}>
                        <Box>
                          <Typography variant="body1">
                            <strong>{option.imei}</strong> - {option.type}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {vehicleInfo} | Модель: {option.model || 'Не указана'}
                          </Typography>
                        </Box>
                      </li>
                    );
                  }}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  filterOptions={(options, state) => {
                    const searchTerm = state.inputValue.toLowerCase();
                    return options.filter(option => 
                      option.imei?.toLowerCase().includes(searchTerm) ||
                      option.type?.toLowerCase().includes(searchTerm) ||
                      option.model?.toLowerCase().includes(searchTerm) ||
                      option.vehicleId?.licensePlate?.toLowerCase().includes(searchTerm)
                    );
                  }}
                  loading={devicesLoading}
                  loadingText="Загрузка устройств..."
                  noOptionsText="Устройства не найдены"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Work Order</InputLabel>
                  <Select
                    value={form.workOrderId || ''}
                    onChange={(e) => setForm({ ...form, workOrderId: e.target.value })}
                    label="Work Order"
                  >
                    <MenuItem value="">None</MenuItem>
                    {workOrders?.map((wo) => (
                      <MenuItem key={wo._id} value={wo._id}>
                        {wo.title} - {wo.status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Assigned To</InputLabel>
                  <Select
                    value={form.assignedTo || ''}
                    onChange={(e) => setForm({ ...form, assignedTo: e.target.value })}
                    label="Assigned To"
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {users?.map((user) => (
                      <MenuItem key={user._id} value={user._id}>
                        {user.fullName} ({user.role})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="new">New</MenuItem>
                    <MenuItem value="in-progress">In Progress</MenuItem>
                    <MenuItem value="waiting-client">Waiting Client</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes (will be added to status history)"
                  multiline
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  helperText={log ? "These notes will be saved to status history when you change status or add a note" : "Initial notes for this log entry"}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel || onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LogForm;